/**
 * SOLID Review
 * S — Strong. One concern: the procedural dice rattle — Web Audio only, no file, no `three`, so the 3D and the 2D scene both play it.
 * O — Good. Tuning the sound (clack count, filter, envelope) is local math in `rattle`.
 * L — N/A — no hierarchy.
 * I — Strong. One method.
 * D — Strong. The context it plays on and the randomness it is made of are handed in (`IConfigDepViewPlatform`); it names no global.
 */

/**
 * THE DICE RATTLE (frontend-framework-oo P1, 2026-10-29, §28.293) — `components/game/diceAudio.ts` until P1, whose module-level
 * `AudioContext` every importer shared. Now an object the `ViewModelFactory` builds once, holding its context as a field: made when
 * the first rattle plays, made again on the next if the browser refused it (an autoplay policy, no Web Audio) — a table with no
 * sound, never a roll handler that throws.
 */
export class DiceAudio {
  private context: AudioContext | null = null;

  constructor(
    private readonly createContext: () => AudioContext,
    private readonly random: () => number,
  ) {}

  private audio(): AudioContext | null {
    try {
      if (this.context === null) this.context = this.createContext();
      return this.context;
    } catch {
      return null;
    }
  }

  /** 6–12 wooden clacks over `durationSec`, denser at the start. */
  rattle(durationSec = 1.1): void {
    const ctx = this.audio();
    if (!ctx) return;

    const numClacks = Math.floor(6 + this.random() * 6); // 6-12 impacts
    for (let i = 0; i < numClacks; i++) {
      // Spread clacks over the duration, denser at start then sparse
      const spacing = durationSec * (0.06 + (i / numClacks) * 0.08);
      const t = ctx.currentTime + i * spacing + this.random() * 0.025;

      // Short white-noise burst per impact
      const frameCount = Math.floor(ctx.sampleRate * 0.045);
      const buf = ctx.createBuffer(1, frameCount, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < frameCount; j++) d[j] = this.random() * 2 - 1;

      const src = ctx.createBufferSource();
      src.buffer = buf;

      // Bandpass → wooden/plastic click character
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 700 + this.random() * 900;
      filter.Q.value = 2.0;

      // Sharp attack, fast exponential decay
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.28 + this.random() * 0.18, t + 0.003);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);

      src.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      src.start(t);
      src.stop(t + 0.06);
    }
  }
}
